import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Heart, Handshake, ChevronRight } from 'lucide-react';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const AboutPage = () => {
  const { t, language } = useLanguage();

  const values = [
    { icon: Target, title_sv: 'Kvalitet', title_en: 'Quality', desc_sv: 'Vi kompromissar aldrig med kvaliteten. Varje produkt vi levererar uppfyller de högsta standarderna.', desc_en: 'We never compromise on quality. Every product we deliver meets the highest standards.' },
    { icon: Heart, title_sv: 'Pålitlighet', title_en: 'Reliability', desc_sv: 'Våra kunder kan alltid lita på att vi levererar i tid och håller vad vi lovar.', desc_en: 'Our customers can always count on us to deliver on time and keep our promises.' },
    { icon: Handshake, title_sv: 'Partnerskap', title_en: 'Partnership', desc_sv: 'Vi bygger långsiktiga relationer med våra kunder baserade på ömsesidig respekt och förståelse.', desc_en: 'We build long-term relationships with our customers based on mutual respect and understanding.' }
  ];

  return (
    <div data-testid="about-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={language === 'sv' 
          ? 'Om Flowingo AB | VVS-leverantör sedan 2022' 
          : 'About Flowingo AB | HVAC Supplier Since 2022'}
        description={language === 'sv'
          ? 'Flowingo AB är en leverantör av VVS-produkter för proffsmarknaden sedan 2022. Vi erbjuder kvalitetsprodukter, pålitliga leveranser och teknisk expertis till installatörer och grossister.'
          : 'Flowingo AB has been a supplier of HVAC products for the professional market since 2022. We offer quality products, reliable deliveries and technical expertise to installers and wholesalers.'}
        language={language}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Om oss' : 'About', url: 'https://flowingo.se/om-oss' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            '@id': 'https://flowingo.se/om-oss',
            name: language === 'sv' ? 'Om Flowingo AB' : 'About Flowingo AB',
            description: 'Leverantör av VVS-produkter för proffsmarknaden sedan 2022',
            isPartOf: { '@id': 'https://flowingo.se/#website' },
            about: { '@id': 'https://flowingo.se/#organization' },
          }
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-6">
          <Link to="/" className="hover:text-stone-900">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">{t('nav.about')}</span>
        </nav>
        <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">
          {language === 'sv' ? 'Om företaget' : 'About the company'}
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
          {language === 'sv' 
            ? 'Om Flowingo AB – Din VVS-partner sedan 2022' 
            : 'About Flowingo AB – Your HVAC Partner Since 2022'}
        </h1>
      </div>

      <section className="relative py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                {language === 'sv' 
                  ? 'Flowingo AB är en leverantör av VVS-produkter för proffsmarknaden.'
                  : 'Flowingo AB is a supplier of HVAC products for the professional market.'}
              </p>
              <p className="text-stone-600 leading-relaxed mb-8">
                {language === 'sv' 
                  ? 'Sedan starten 2022 har vi byggt upp ett sortiment av högkvalitativa VVS-produkter och etablerat oss som en pålitlig partner för installatörer, grossister och fastighetsbolag.' 
                  : 'Since our start in 2022, we have built a range of high-quality HVAC products and established ourselves as a reliable partner for installers, wholesalers and property companies.'}
              </p>
              <Link to="/produkter">
                <Button className="bg-stone-900 text-white hover:bg-stone-800 rounded-none">
                  {t('hero.cta')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="aspect-video lg:aspect-square overflow-hidden bg-stone-200">
              <img src="/images/vatten-om-oss.jpg" alt="Flowingo VVS-produkter" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">{language === 'sv' ? 'Vår riktning' : 'Our direction'}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-6">{t('about.vision')}</h2>
            <p className="text-stone-600 text-lg leading-relaxed">{t('about.visionText')}</p>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">{language === 'sv' ? 'Vårt fundament' : 'Our foundation'}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">{t('about.values')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="bg-white p-8 border border-stone-200 hover:border-stone-300 transition-colors" data-testid={`value-${idx}`}>
                <div className="w-14 h-14 bg-stone-100 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-stone-600" />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-3">{language === 'sv' ? value.title_sv : value.title_en}</h3>
                <p className="text-stone-600 leading-relaxed">{language === 'sv' ? value.desc_sv : value.desc_en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 aspect-video overflow-hidden bg-stone-200">
              <img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800" alt="B2B partnership" className="w-full h-full object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">{language === 'sv' ? 'Affärsrelationer' : 'Business relations'}</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-6">{t('about.b2b')}</h2>
              <p className="text-stone-600 leading-relaxed mb-6">{t('about.b2bText')}</p>
              <ul className="space-y-3 text-stone-600">
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-2 shrink-0" />{language === 'sv' ? 'Snabba leveranser över hela Norden' : 'Fast deliveries throughout the Nordic region'}</li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-2 shrink-0" />{language === 'sv' ? 'Teknisk support och rådgivning' : 'Technical support and advice'}</li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-2 shrink-0" />{language === 'sv' ? 'Konkurrenskraftiga priser för volymer' : 'Competitive prices for volumes'}</li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-stone-400 mt-2 shrink-0" />{language === 'sv' ? 'Skräddarsydda lösningar och private labels' : 'Tailored solutions and private labels'}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-400 text-sm uppercase tracking-widest mb-4">{language === 'sv' ? 'Ta nästa steg' : 'Take the next step'}</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">{language === 'sv' ? 'Vill du veta mer?' : 'Want to know more?'}</h2>
          <p className="text-stone-400 mb-8">{language === 'sv' ? 'Kontakta oss så berättar vi mer om hur vi kan hjälpa ditt företag.' : 'Contact us and we\'ll tell you more about how we can help your business.'}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/kontakt">
              <Button size="lg" className="bg-white text-stone-900 hover:bg-stone-100 rounded-none">{t('contact.title')} <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </Link>
            <Link to="/produkter">
              <Button variant="outline" size="lg" className="border-stone-600 text-white hover:bg-stone-800 rounded-none">{t('hero.cta')}</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
